import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { Card } from '@/common/components/Card/Card';
import { ControlledDateField } from '@/common/components/Fields/ControlledDateField/ControlledDateField';
import { ControlledSelectField } from '@/common/components/Fields/ControlledSelectField/ControlledSelectField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { determineKinships } from '@/common/constants/kinship';
import {
  TSimulateAddFamilyMemberFormValues,
  simulateAddFamilyMemberSchema,
} from '@/common/containers/Dashboard/Insurance/InsuranceHomePage/InsurancePolicyDetail/SimulateFamilyMembersModal/SimulateAddFamilyMemberForm/SimulateAddFamilyMemberForm.schema';
import { useMarket } from '@/common/hooks/useMarket';
import { MenhirFamilyMemberType } from '@/common/integrations/menhir/employee/employee/employee.types';
import { TMemberSimulation } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { formatDateISO } from '@/common/utils/date';
import { zodResolver } from '@hookform/resolvers/zod';
import { styles } from './SimulateAddFamilyMemberForm.styles';

export const SimulateAddFamilyMemberForm = ({
  onClose,
  onAddMember,
  hasSpouse,
  loading,
}: TSimulateAddFamilyMemberFormProps) => {
  const { t } = useTranslation();

  const { slug } = useMarket();
  const kinshipList = useMemo(() => determineKinships(slug), [slug]);
  const kinshipOptions = kinshipList.map((kinship) => ({
    ...kinship,
    label: t(kinship.label),
  }));

  const methods = useForm<TSimulateAddFamilyMemberFormValues>({
    mode: 'onTouched',
    defaultValues: {
      shortName: '',
      type: undefined,
      birthdate: undefined,
    },
    resolver: zodResolver(simulateAddFamilyMemberSchema(slug, hasSpouse)),
  });

  const handleAddMember = (form: TSimulateAddFamilyMemberFormValues) => {
    const newMember: Partial<TMemberSimulation> = {
      shortName: form.shortName,
      type: form.type?.value as MenhirFamilyMemberType,
      birthdate: formatDateISO(form.birthdate),
      selected: true,
      isUserAdded: true,
    };
    onAddMember(newMember as TMemberSimulation);
  };

  return (
    <Card plain>
      <Stack gap="md">
        <FormProvider {...methods}>
          <ControlledTextField
            name="shortName"
            label={t(
              'dashboard.insurance_detail.simulate_family_modal.short_name'
            )}
          />
          <ControlledSelectField
            name="type"
            label={t(
              'dashboard.insurance_detail.simulate_family_modal.kinship_degree'
            )}
            options={kinshipOptions}
            hint={
              <Translate
                i18nKey="dashboard.insurance_detail.simulate_family_modal.kinship_degree.hint"
                components={{ strong: <BodyText bold color="neutral20" /> }}
              />
            }
          />
          <ControlledDateField
            name="birthdate"
            label={t(
              'dashboard.insurance_detail.simulate_family_modal.birthdate'
            )}
          />
          <Stack horizontal justify="between" gap="md">
            <Button
              variant="secondary"
              onPress={() => {
                onClose();
              }}
              disabled={loading}
            >
              {t('general.navigation.cancel')}
            </Button>
            <Button
              className={styles.button}
              variant="primary"
              onPress={methods.handleSubmit(handleAddMember)}
              loading={loading}
            >
              {t('dashboard.insurance_detail.simulate_family_modal.add_button')}
            </Button>
          </Stack>
        </FormProvider>
      </Stack>
    </Card>
  );
};

export type TSimulateAddFamilyMemberFormProps = {
  onClose: () => void;
  onAddMember: (person: TMemberSimulation) => void;
  hasSpouse: boolean;
  loading: boolean;
};
